/**
 * Timezone-safe date parsing and formatting utilities.
 *
 * Date-only strings ("2024-01-15") are parsed as LOCAL midnight
 * to prevent day-shift in negative-UTC timezones. Full timestamps
 * are parsed normally so toLocale*() converts to the user's local tz.
 *
 * All formatting uses `undefined` locale → browser's own locale,
 * giving each user dates in their preferred language and format.
 */

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function parseDate(val) {
  if (!val) return new Date(NaN);
  if (val instanceof Date) return val;
  const s = String(val);
  if (DATE_ONLY_RE.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(s);
}

export function fmtDate(val, opts) {
  const d = parseDate(val);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString(undefined, opts || { day: 'numeric', month: 'short', year: 'numeric' });
}

export function fmtDateShort(val) {
  const d = parseDate(val);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function fmtDateTime(val) {
  const d = parseDate(val);
  if (isNaN(d)) return '—';
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function fmtDateFile(val) {
  const d = parseDate(val);
  if (isNaN(d)) return 'unknown';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Minutes east of UTC (e.g. 330 for IST). Send to backend as ?tz= */
export const tzOffset = -new Date().getTimezoneOffset();

export const tzParam = `tz=${tzOffset}`;

/** Local calendar date as YYYY-MM-DD (for range filters aligned with the user's timezone). */
export function toYmdLocal(val) {
  const d = val instanceof Date ? val : parseDate(val);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Add `deltaDays` to a YYYY-MM-DD string in local calendar terms. */
export function addDaysYmd(ymd, deltaDays) {
  const d = parseDate(ymd);
  if (isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + deltaDays);
  return toYmdLocal(d);
}
